///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { SectorFeature } from './sector-feature';
import { LinkFeature } from '../link/link-feature';
import { GtthStepGroupEnum, PdbEngineProductSectorConfigDTO } from '../../../../generated/shared-types.model';
import { GtthPosition } from '../position';
import { NodeFeature } from '../node/node-feature';
import { GtthSectorObstruction } from './sector-obstruction.enum';
import { AngleUtils } from '../angle-utils';
import { GtthHistoryService } from '../../engine/history-service';
import { isDefined } from '@siklu/sui-lib/utils';

export class DeviceSectorsFeature {
    private readonly sectors: SectorFeature[];
    private sectorBounds: { min: number; max: number }[];
    private showAllSectors: boolean;

    constructor(
        private readonly sectorIndex: number,
        private readonly sectorLayerIndex: number,
        private readonly stationId: number,
        private readonly sectorPosition: GtthPosition,
        private readonly obstructionAngle: number,
        private readonly historyService: GtthHistoryService,
        private readonly sectorsConfig: PdbEngineProductSectorConfigDTO[]
    ) {
        this.sectors = this.sectorsConfig.map((sectorConfig, index) => {
            const sector = new SectorFeature(
                this,
                this.sectorIndex,
                index,
                this.sectorLayerIndex,
                this.sectorPosition,
                this.historyService
            );

            if (this.obstructionAngle > 0) {
                if (index === 1) {
                    sector.setObstruction(this.obstructionAngle / 2, GtthSectorObstruction.RIGHT);
                }
                if (index === 2) {
                    sector.setObstruction(this.obstructionAngle / 2, GtthSectorObstruction.LEFT);
                }
            }

            return sector;
        });
    }

    addLink(link: LinkFeature): void {
        this.sectors[link.getSectorInDevice()]._addLink(link);
        this.sectors.forEach((sector) => sector.recalculateSectorDistance(link.getBuUsedProduct()));
    }

    draw(showAllSectors: boolean): void {
        this.showAllSectors = showAllSectors;
        let deviceSectorDirection;
        let directionFromFrozenSector = false;

        for (let i = 0; i < this.sectorsConfig.length; i++) {
            if (this.sectors[i]._isActiveSector() && !directionFromFrozenSector) {
                directionFromFrozenSector = this.sectors[i]._isFrozen();
                deviceSectorDirection = AngleUtils.normalize(this.sectors[i].getAngle() - this.sectorsConfig[i].azimuthOffset);
            }
        }

        for (let i = 0; i < this.sectorsConfig.length; i++) {
            if (!isDefined(deviceSectorDirection)) {
                break;
            }
            const correctedSectorAngle = AngleUtils.normalize(deviceSectorDirection + this.sectorsConfig[i].azimuthOffset);
            this.sectors[i].setAngle(correctedSectorAngle);
        }

        this.sectors.forEach((sector: SectorFeature) => this.redrawInnerSector(sector));
    }

    hide(): void {
        this.sectors.forEach((sector: SectorFeature) => sector._hide());
    }

    getSectorStationId(): number {
        return this.stationId;
    }

    refreshColor(): void {
        this.sectors.filter((sector: SectorFeature) => sector._isActiveSector()).forEach((sector: SectorFeature) => sector.refreshColor());
    }

    getDeviceSector(sectorInDevice: number): SectorFeature {
        return this.sectors[sectorInDevice];
    }

    getActiveSectors(): SectorFeature[] {
        return this.sectors.filter((sector: SectorFeature) => isDefined(sector));
    }

    getSectorsCount(): number {
        return this.sectors.filter((sector: SectorFeature) => sector._isActiveSector()).length;
    }

    hasSectorInDevice(sectorInDevice: number): boolean {
        return isDefined(this.sectors[sectorInDevice]);
    }

    changeSectorsDirection(sectorInDevice: number, newSectorAngle: number): boolean {
        const changedSector = this.sectors[sectorInDevice];
        const delta = newSectorAngle - changedSector.getAngle();

        const angleBounds = this.getAngleMinMaxRange(sectorInDevice);

        if (AngleUtils.isBetween(newSectorAngle, angleBounds.min, angleBounds.max)) {
            this.sectors.forEach((sector: SectorFeature) => sector.setAngle(sector.getAngle() + delta));
            return true;
        } else {
            return false;
        }
    }

    removeLinkFromSector(line: LinkFeature): void {
        if (this.hasSectorInDevice(line.getSectorInDevice())) {
            this.sectors[line.getSectorInDevice()]._removeLink(line);
            this.redrawInnerSector(this.sectors[line.getSectorInDevice()]);
        }
    }

    linkFreezeChanged(link: LinkFeature) {
        this.redrawInnerSector(this.sectors[link.getSectorInDevice()]);
    }

    changeAngle(sectorDelta: number): void {
        this.sectors
            .filter((sector) => sector._isActiveSector())
            .forEach((s: SectorFeature) => {
                s.setAngle(s.getAngle() + sectorDelta);
            });
    }

    getAngleMinMaxRange(sectorInDevice: number): { max: number; min: number } {
        if (!this.sectorBounds || !isDefined(this.sectorBounds[sectorInDevice])) {
            this.calculateAllSectorBounds();
        }

        return this.sectorBounds[sectorInDevice];
    }

    private redrawInnerSector(sector: SectorFeature): void {
        const atLeastOneSectorIsActive = this.sectors.some((innerSector) => innerSector._isActiveSector());
        const atLeastOneSectorIsFrozen = this.sectors.some((innerSector) => innerSector._isFrozen());

        // show frozen sectors in any case
        const frozen = sector._isFrozen();
        // show active sectors only when PLAN/BOM steps activated and design exist
        const active =
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
            this.historyService.logicalTopology.isShowPlanningMode &&
            sector._isActiveSector();
        // show not active sectors when PLAN/BOM steps activated and
        //  - there is at least one active sector and design is active
        //  - there is at leat one frozen sector
        const notActive =
            this.showAllSectors &&
            this.historyService.selectedStepNumber !== GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
            !sector._isActiveSector() &&
            atLeastOneSectorIsActive &&
            (this.historyService.logicalTopology.isShowPlanningMode || atLeastOneSectorIsFrozen);

        if (frozen || active || notActive) {
            sector._draw();
        } else {
            sector._hide();
        }
    }

    private calculateAllSectorBounds(): void {
        const sectorBounds = this.sectors.map((sector) => this.calculateSectorAngleBounds(sector));
        this.sectorBounds = AngleUtils.calculateAllSectorsBounds(
            sectorBounds,
            this.sectorsConfig.map((config) => config.azimuthOffset)
        );
    }

    private calculateSectorAngleBounds(sector: SectorFeature): { min: number; max: number } {
        const stationsInSector = sector
            .getLinks()
            .map((link) => (link.getFromId() !== this.stationId ? link.getFromStation() : link.getToStation()));

        const sectorCenter = this.sectorPosition.toLatLng(google);
        const linksAngleArr = stationsInSector.map((station: NodeFeature) =>
            AngleUtils.normalize(google.maps.geometry.spherical.computeHeading(sectorCenter, station.getPosition().toLatLng(google)))
        );

        if (linksAngleArr.length === 0) {
            return { min: undefined, max: undefined };
        }

        return AngleUtils.calculateSectorMinMax(linksAngleArr, sector.obstructionAngle, sector.obstructionDirection);
    }
}
